import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { findNavigationItemByPageKey } from '../config/app-navigation';
import { environment } from '../../../environments/environment';

export interface PosTerminalSummary {
  id: number;
  label: string;
  pos_id: string;
  location: { id: number; display_name: string } | null;
}

export interface AssignedLocationSummary {
  id: number;
  display_name: string;
  prefix: string | null;
}

export interface User {
  id: number;
  name: string;
  email: string;
  wp_user_id: number;
  roles: string[];
  wp_roles: string[];
  capabilities: string[];
  page_access: Record<string, boolean>;
  allowed_page_keys: string[];
  default_page_key: string | null;
  feature_access: Record<string, boolean>;
  // Alcance de datos resuelto para Gestión Comercial (ver RecordScopeResolver).
  // 'all' = ve todo · 'location' = solo sus sucursales · 'own' = solo asignados ·
  // 'none' = sin alcance (no ve registros). El backend es la fuente de verdad;
  // el front lo usa solo para pre-setear/lockear filtros y el estado vacío.
  // Es por página: `default` (alcance general del/los grupo/s) + overrides por
  // page_key en `pages`. Consultar con `gcRecordScopeFor(pageKey)`.
  gc_record_scope: GcRecordScopeMap;
  assigned_locations: AssignedLocationSummary[];
  available_pos_terminals: PosTerminalSummary[];
}

export type GcRecordScope = 'all' | 'team' | 'location' | 'own' | 'none';

export interface GcRecordScopeMap {
  default: GcRecordScope;
  pages: Record<string, GcRecordScope>;
}

/**
 * Resultado discriminado de checkSession().
 *  - authenticated:   /me devolvió 200 y hay sesión.
 *  - unauthenticated: /me devolvió 401/403 (sin sesión, retry por SSO).
 *  - error:           cualquier otro fallo (5xx, network, CORS). El caller
 *                     debe NO hacer auth.login() para no entrar en loop.
 */
export type SessionCheckResult =
  | { status: 'authenticated' }
  | { status: 'unauthenticated' }
  | { status: 'error'; httpStatus: number };

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private csrfReadyPromise: Promise<void> | null = null;

  readonly currentUser = signal<User | null>(null);

  // Sesión caída detectada (401 confirmado por re-check). El layout muestra el
  // banner "Tu sesión expiró → Reingresar". Se resetea al re-bootear tras el SSO.
  readonly sessionExpired = signal(false);
  private recheckInFlight = false;

  readonly isAuthenticated = computed(() => this.currentUser() !== null);
  readonly userRoles = computed(() => this.currentUser()?.roles ?? []);
  readonly userWordPressRoles = computed(() => this.currentUser()?.wp_roles ?? []);
  readonly userCapabilities = computed(() => this.currentUser()?.capabilities ?? []);
  readonly pageAccess = computed(() => this.currentUser()?.page_access ?? {});
  readonly allowedPageKeys = computed(() => this.currentUser()?.allowed_page_keys ?? []);
  readonly defaultPageKey = computed(() => this.currentUser()?.default_page_key ?? null);
  readonly featureAccess = computed(() => this.currentUser()?.feature_access ?? {});
  readonly availablePosTerminals = computed(() => this.currentUser()?.available_pos_terminals ?? []);
  readonly userAssignedLocations = computed(() => this.currentUser()?.assigned_locations ?? []);

  /**
   * Alcance de datos GC para una página concreta (page_key, p.ej.
   * 'gestion-comercial.socios'). El override por página gana sobre el alcance
   * general del grupo; sin override, cae al `default`. Sin sesión → 'none'.
   */
  gcRecordScopeFor(pageKey: string): GcRecordScope {
    const scope = this.currentUser()?.gc_record_scope;
    if (!scope) {
      return 'none';
    }
    return scope.pages[pageKey] ?? scope.default ?? 'none';
  }

  /**
   * Consulta /api/auth/me y devuelve un resultado discriminado para que el
   * caller pueda distinguir "sin sesión" (retry por SSO) de "error real"
   * (mostrar pantalla de fallback). Siempre resuelve, nunca lanza.
   */
  checkSession(): Promise<SessionCheckResult> {
    return new Promise((resolve) => {
      this.http.get<User>(`${environment.apiUrl}/auth/me`).subscribe({
        next: (user) => {
          this.currentUser.set(user);
          resolve({ status: 'authenticated' });
        },
        error: (err: HttpErrorResponse) => {
          this.currentUser.set(null);

          if (err.status === 401 || err.status === 403) {
            resolve({ status: 'unauthenticated' });
            return;
          }

          console.error('[auth] checkSession failed', err);
          resolve({ status: 'error', httpStatus: err.status ?? 0 });
        },
      });
    });
  }

  /**
   * Ante un 401 en cualquier request, re-verifica la sesión contra /auth/me:
   * si confirma que no hay sesión → marca `sessionExpired` (el layout muestra el
   * banner de reingreso). Si la sesión sigue viva (401 espurio) o el fallo es un
   * error real (5xx/red), NO marca expirado. Guard anti-concurrencia: múltiples
   * 401 simultáneos disparan un solo re-check.
   */
  async handlePossibleSessionExpiry(): Promise<void> {
    if (this.recheckInFlight || this.sessionExpired()) {
      return;
    }

    this.recheckInFlight = true;
    try {
      const result = await this.checkSession();
      if (result.status === 'unauthenticated') {
        this.sessionExpired.set(true);
      }
    } finally {
      this.recheckInFlight = false;
    }
  }

  ensureCsrfCookie(forceRefresh = false): Promise<void> {
    if (forceRefresh || !this.csrfReadyPromise) {
      this.csrfReadyPromise = firstValueFrom(
        this.http.get(`${environment.backendUrl}/sanctum/csrf-cookie`, {
          responseType: 'text',
        }),
      ).then(() => undefined);
    }

    return this.csrfReadyPromise;
  }

  /**
   * Redirige el browser a WordPress para iniciar el flujo SSO.
   * La URL de entrada SSO es configurable por ambiente para desacoplar
   * la aplicación de la puerta real de login de WordPress.
   */
  login(): void {
    window.location.href = environment.wordpressSsoEntryUrl;
  }

  /**
   * Cierra sesión en el sistema interno y redirige al logout de WordPress.
   */
  async logout(): Promise<void> {
    try {
      await this.ensureCsrfCookie();

      const { logout_url } = await firstValueFrom(
        this.http.delete<{ logout_url: string }>(`${environment.apiUrl}/auth/logout`),
      );

      this.currentUser.set(null);
      window.location.href = logout_url;
    } catch {
      this.currentUser.set(null);
      window.location.href = environment.wordpressUrl;
    }
  }

  canAccessPage(pageKey: string): boolean {
    return this.pageAccess()[pageKey] === true;
  }

  /**
   * Visibilidad de una funcionalidad (acción/campo) gateable. La resolución
   * (bypass admin / default por feature / allowlist) la hace el backend; acá
   * solo se lee el mapa precalculado. Una feature ausente del mapa = oculta.
   */
  can(featureKey: string): boolean {
    return this.featureAccess()[featureKey] === true;
  }

  defaultAccessibleRoute(): string | null {
    const defaultPageKey = this.defaultPageKey();

    if (!defaultPageKey) {
      return null;
    }

    return findNavigationItemByPageKey(defaultPageKey)?.path ?? null;
  }
}
